import { Plus, Search } from "lucide-react";
import { useGetIdentity, useTranslate } from "ra-core";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const greetingOfHour = (hour: number) =>
  hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";

/**
 * Greeting on the left, a company search and the "new lead" button on the
 * right. On a phone the three stack: greeting, then the search full width,
 * then the button full width.
 */
export const DashboardHeader = ({ onNewLead }: { onNewLead: () => void }) => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { data: identity } = useGetIdentity();
  const [query, setQuery] = useState("");

  const firstName = identity?.fullName?.split(" ")[0] ?? "";
  const greeting = translate(
    `crm.dashboard.greeting.${greetingOfHour(new Date().getHours())}`,
    { name: firstName },
  ).replace(/,\s*$/, "");

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const q = query.trim();
    if (!q) return;
    navigate(`/companies?filter=${encodeURIComponent(JSON.stringify({ q }))}`);
  };

  return (
    <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-4">
      <h1 className="text-2xl font-bold tracking-tight text-brand-navy md:text-3xl">
        {greeting}
      </h1>
      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
        <form onSubmit={handleSearch} role="search" className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={translate("crm.dashboard.search_placeholder")}
            aria-label={translate("crm.dashboard.search_label")}
            className="h-10 w-full rounded-lg bg-white pl-9 md:w-72"
          />
        </form>
        <Button
          type="button"
          className="h-10 w-full rounded-lg md:w-auto"
          onClick={onNewLead}
        >
          <Plus className="size-4" />
          {translate("crm.dashboard.new_lead")}
        </Button>
      </div>
    </div>
  );
};
